import React, { useState, useEffect, useRef, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { Box, Group, Loader, TextInput, MultiSelect, Badge, ActionIcon } from '@mantine/core';
import { IconSearch, IconAdjustmentsHorizontal } from '@tabler/icons-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import '../styles/FullCalendarView.css';

// Interfaces
interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  image_url?: string;
  event_types?: { id: number; name: string }[];
  dance_styles?: { id: number; name: string }[];
  dance_style_ids?: number[];
  slug?: string;
  area?: {
    id: number;
    name: string;
    country?: {
      id: number;
      name: string;
      flag_emoji?: string;
    };
  };
  [key: string]: any;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  url: string;
  backgroundColor: string;
  borderColor: string;
}

interface CalendarMetadata {
  month: number;
  year: number;
}

// Cache interface for month-based calendar events
interface CalendarCache {
  [key: string]: CalendarEvent[];
}

interface FilterOptions {
  name?: boolean;
  danceStyles?: boolean;
  eventTypes?: boolean;
}

interface FullCalendarViewProps {
  entityType: 'area' | 'organizer' | 'artist' | 'dance_style';
  entityId: number;
  entitySlug: string;
  initialEvents: Event[];
  initialCalendarMetadata?: CalendarMetadata;
  eventTypes: { id: number; name: string }[];
  danceStyles: { id: number; name: string }[];
  initialFilters?: {
    name?: string;
    dance_style_ids?: number[];
    event_type_ids?: number[];
  };
  onEventClick: (event: Event) => void;
  filterOptions?: FilterOptions;
  // Add these props to receive data from the parent component
  currentEvents?: Event[];
  upcomingEvents?: Event[];
  pastEvents?: Event[];
}

const FullCalendarView: React.FC<FullCalendarViewProps> = ({
  entityType,
  entityId,
  entitySlug,
  initialEvents,
  initialCalendarMetadata,
  eventTypes = [],
  danceStyles = [],
  initialFilters = {},
  onEventClick,
  filterOptions = { name: true, danceStyles: true, eventTypes: true },
  // These props allow us to receive updated data from parent
  currentEvents,
  upcomingEvents = [],
  pastEvents = [],
}) => {
  // == STATE DECLARATIONS ==
  // General states
  const [isLoading, setIsLoading] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [debouncingSearch, setDebouncingSearch] = useState(false);

  // Calendar-specific states
  const [eventsCache, setEventsCache] = useState<CalendarCache>({});
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState<number>(
    initialCalendarMetadata?.month || new Date().getMonth() + 1,
  );
  const [currentCalendarYear, setCurrentCalendarYear] = useState<number>(
    initialCalendarMetadata?.year || new Date().getFullYear(),
  );
  const calendarRef = useRef<any>(null);

  // Filter states
  const [searchName, setSearchName] = useState(initialFilters?.name || '');
  const [searchNameInput, setSearchNameInput] = useState(initialFilters?.name || '');
  const [selectedDanceStyles, setSelectedDanceStyles] = useState(initialFilters?.dance_style_ids || []);
  const [selectedEventTypes, setSelectedEventTypes] = useState(initialFilters?.event_type_ids || []);
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  // == CONSTANTS ==
  const DEBOUNCE_DELAY = 500; // 500ms debounce delay
  const baseOnlyCounts = ['upcoming_total_count', 'past_total_count', 'artists_total_count', 'organizers_total_count'];

  // == COMPUTED VALUES (based on state) ==
  // Options for filters
  const eventTypeOptions = eventTypes.map((et) => ({
    value: et.id.toString(),
    label: et.name,
  }));

  const danceStyleOptions = danceStyles.map((ds) => ({
    value: ds.id.toString(),
    label: ds.name,
  }));

  // Calculate active filter count
  const activeFilterCount =
    (searchName && filterOptions.name ? 1 : 0) +
    (filterOptions.danceStyles ? selectedDanceStyles.length : 0) +
    (filterOptions.eventTypes ? selectedEventTypes.length : 0);

  // == UTILITY FUNCTIONS ==
  // Build filter parameters for API calls
  const buildFilterParams = useCallback(() => {
    const filterParams: Record<string, any> = {};

    if (searchName && filterOptions.name) filterParams.name = searchName;
    if (selectedDanceStyles.length && filterOptions.danceStyles) filterParams.dance_style_ids = selectedDanceStyles;
    if (selectedEventTypes.length && filterOptions.eventTypes) filterParams.event_type_ids = selectedEventTypes;

    return Object.keys(filterParams).length > 0 ? filterParams : undefined;
  }, [searchName, selectedDanceStyles, selectedEventTypes, filterOptions]);

  // Create a cache key based on year and month
  const createCacheKey = useCallback(
    (year: number, month: number) => {
      const filterParams = buildFilterParams();
      const filterString = filterParams ? JSON.stringify(filterParams) : '';
      return `${year}-${month}-${filterString}`;
    },
    [buildFilterParams],
  );

  // Format events for calendar display
  const formatEventsForCalendar = useCallback((events: Event[]): CalendarEvent[] => {
    return events.map((event) => {
      // Safely access the flag emoji from the nested properties
      const flagEmoji = event.area?.country?.flag_emoji || '';

      return {
        id: event.id?.toString() || `event-${Math.random().toString(36).substr(2, 9)}`,
        title: `${flagEmoji} ${event.name || 'Untitled Event'}`,
        start: event.event_start_date,
        end: event.event_end_date,
        url: event.slug ? `/events/${event.slug}` : '#',
        backgroundColor: '#ea580c',
        borderColor: '#ea580c',
      };
    });
  }, []);

  // == EVENT HANDLERS ==
  // Apply filters - defined first so it can be used in debounce
  const applyFilters = useCallback(() => {
    setIsLoading(true);

    const filterParams = buildFilterParams();

    // Clear the cache when filters change
    setEventsCache({});

    // Build the route path based on entity type
    let routePath = '';
    switch (entityType) {
      case 'area':
        routePath = `/city/${entitySlug}`;
        break;
      case 'organizer':
        routePath = `/o/${entitySlug}`;
        break;
      case 'artist':
        routePath = `/artists/${entitySlug}`;
        break;
      case 'dance_style':
        routePath = `/dance_styles/${entitySlug}`;
        break;
      default:
        routePath = window.location.pathname;
    }

    router.visit(routePath, {
      data: {
        active_tab: 'calendar',
        month: currentCalendarMonth,
        year: currentCalendarYear,
        filter: filterParams,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['calendar_events', 'calendar_metadata', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  }, [buildFilterParams, currentCalendarMonth, currentCalendarYear, entitySlug, entityType, baseOnlyCounts]);

  // Debounced apply filters function
  const debouncedApplyFilters = useCallback(
    debounce((callback: () => void) => {
      setDebouncingSearch(false);
      callback();
    }, DEBOUNCE_DELAY),
    [],
  );

  // Handle search name input change with debounce
  const handleSearchNameChange = useCallback(
    (value: string) => {
      setSearchNameInput(value);
      setDebouncingSearch(true);
      setSearchName(value);
      debouncedApplyFilters(applyFilters);
    },
    [debouncedApplyFilters, applyFilters],
  );

  // Handle dance style selection with debounce
  const handleDanceStyleChange = useCallback(
    (value: string[]) => {
      setSelectedDanceStyles(value);
      setDebouncingSearch(true);
      debouncedApplyFilters(applyFilters);
    },
    [debouncedApplyFilters, applyFilters],
  );

  // Handle event type selection with debounce
  const handleEventTypeChange = useCallback(
    (value: string[]) => {
      setSelectedEventTypes(value);
      setDebouncingSearch(true);
      debouncedApplyFilters(applyFilters);
    },
    [debouncedApplyFilters, applyFilters],
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchName('');
    setSearchNameInput('');
    setSelectedDanceStyles([]);
    setSelectedEventTypes([]);
    setDebouncingSearch(true);
    debouncedApplyFilters(applyFilters);

    // Clear the events cache when filters change
    setEventsCache({});
  }, [debouncedApplyFilters, applyFilters]);

  // Fetch calendar events for a specific month and year
  const fetchCalendarEvents = useCallback(
    (month: number, year: number) => {
      setIsLoading(true);

      const filterParams = buildFilterParams();
      const cacheKey = createCacheKey(year, month);

      // Check if we have cached data for this month/year/filter combination
      if (eventsCache[cacheKey]) {
        setCalendarEvents(eventsCache[cacheKey]);
        setIsLoading(false);
        return;
      }

      // Build the route path based on entity type
      let routePath = '';
      switch (entityType) {
        case 'area':
          routePath = `/city/${entitySlug}`;
          break;
        case 'organizer':
          routePath = `/o/${entitySlug}`;
          break;
        case 'artist':
          routePath = `/artists/${entitySlug}`;
          break;
        case 'dance_style':
          routePath = `/dance_styles/${entitySlug}`;
          break;
        default:
          routePath = window.location.pathname;
      }

      router.visit(routePath, {
        data: {
          active_tab: 'calendar',
          month: month,
          year: year,
          filter: filterParams,
        },
        preserveState: true,
        preserveScroll: true,
        only: ['calendar_events', 'calendar_metadata', ...baseOnlyCounts],
        onFinish: () => {
          setIsLoading(false);
        },
      });
    },
    [buildFilterParams, createCacheKey, entitySlug, entityType, eventsCache, baseOnlyCounts],
  );

  // Handle dates change in calendar (when user navigates to a different month)
  const handleDatesSet = useCallback(
    (dateInfo: { view: { currentStart: Date } }) => {
      const newStart = dateInfo.view.currentStart;
      const m = newStart.getMonth() + 1;
      const y = newStart.getFullYear();

      // Check if this is coming from a navigation action
      if (m !== currentCalendarMonth || y !== currentCalendarYear) {
        setCurrentCalendarMonth(m);
        setCurrentCalendarYear(y);

        // Directly fetch data rather than relying on the effect
        fetchCalendarEvents(m, y);
      }
    },
    [currentCalendarMonth, currentCalendarYear, fetchCalendarEvents],
  );

  // Create a debounce function
  function debounce(func: Function, delay: number) {
    let timeoutId: NodeJS.Timeout;
    return function (...args: any[]) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  // == EFFECTS ==
  // Update calendar events when currentEvents prop changes
  useEffect(() => {
    if (currentEvents && currentEvents.length > 0) {
      const formatted = formatEventsForCalendar(currentEvents);
      setCalendarEvents(formatted);

      // Cache the formatted events
      const cacheKey = createCacheKey(
        initialCalendarMetadata?.year || currentCalendarYear,
        initialCalendarMetadata?.month || currentCalendarMonth,
      );

      setEventsCache((prev) => ({
        ...prev,
        [cacheKey]: formatted,
      }));
    }
  }, [
    currentEvents,
    formatEventsForCalendar,
    createCacheKey,
    initialCalendarMetadata,
    currentCalendarMonth,
    currentCalendarYear,
  ]);

  // Initialize calendar with initial events
  useEffect(() => {
    if (initialEvents && initialEvents.length > 0) {
      const formatted = formatEventsForCalendar(initialEvents);
      setCalendarEvents(formatted);

      // Cache the initial events
      const cacheKey = createCacheKey(
        initialCalendarMetadata?.year || new Date().getFullYear(),
        initialCalendarMetadata?.month || new Date().getMonth() + 1,
      );

      setEventsCache((prev) => ({
        ...prev,
        [cacheKey]: formatted,
      }));
    }
  }, [initialEvents, formatEventsForCalendar, createCacheKey, initialCalendarMetadata]);

  // Update calendar view when month/year changes
  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      // Use changeView instead of gotoDate to force a refresh
      api.changeView('dayGridMonth', new Date(currentCalendarYear, currentCalendarMonth - 1, 1));
    }
  }, [currentCalendarMonth, currentCalendarYear]);

  // Fetch calendar events when initially mounted if no initial events
  useEffect(() => {
    if (!initialEvents || initialEvents.length === 0) {
      fetchCalendarEvents(currentCalendarMonth, currentCalendarYear);
    }
  }, [fetchCalendarEvents, initialEvents, currentCalendarMonth, currentCalendarYear]);

  // == RENDER ==
  return (
    <Box mt="0" style={{ height: 'auto' }}>
      {/* Search and Filter Bar */}
      <Group justify="apart" mb="lg">
        {/* {filterOptions.name && (
          <TextInput
            placeholder={searchNameInput ? undefined : 'Search events...'}
            value={searchNameInput}
            onChange={(e) => handleSearchNameChange(e.currentTarget.value)}
            style={{ flex: 1 }}
            leftSection={<IconSearch size={16} />}
            rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
          />
        )} */}

        {/* {(filterOptions.danceStyles || filterOptions.eventTypes) && (
          <Box position="relative">
            <ActionIcon variant="light" size="xl" color="orange" onClick={() => setFilterDrawerOpened(true)}>
              <IconAdjustmentsHorizontal />
            </ActionIcon>
            {activeFilterCount > 0 && (
              <Badge color="orange" size="sm" radius="xl" style={{ position: 'absolute', top: -10, right: -10 }}>
                {activeFilterCount}
              </Badge>
            )}
          </Box>
        )} */}
      </Group>

      {/* Filter Controls - inline filters instead of drawer */}
      {filterDrawerOpened && (
        <Box mb="lg" p="md" style={{ border: '1px solid #ea580c', borderRadius: '8px' }}>
          {filterOptions.danceStyles && (
            <MultiSelect
              label="Dance style"
              data={danceStyleOptions}
              value={selectedDanceStyles}
              onChange={handleDanceStyleChange}
              placeholder={selectedDanceStyles.length ? undefined : 'Any dance style'}
              clearable
              searchable
              rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
              mb="md"
            />
          )}

          {filterOptions.eventTypes && (
            <MultiSelect
              label="Event type"
              data={eventTypeOptions}
              value={selectedEventTypes}
              onChange={handleEventTypeChange}
              placeholder={selectedEventTypes.length ? undefined : 'Any event type'}
              clearable
              searchable
              rightSection={debouncingSearch && <Loader size="xs" color="orange" />}
              mb="md"
            />
          )}

          <Group position="right" mt="sm">
            <Badge
              color="orange"
              size="lg"
              style={{ cursor: 'pointer' }}
              onClick={clearFilters}
              rightSection={
                <ActionIcon size="xs" color="orange" variant="transparent">
                  ×
                </ActionIcon>
              }
            >
              Clear filters
            </Badge>
            <Badge color="orange" size="lg" style={{ cursor: 'pointer' }} onClick={() => setFilterDrawerOpened(false)}>
              Close
            </Badge>
          </Group>
        </Box>
      )}

      {/* Calendar View */}
      {isLoading || debouncingSearch ? (
        <Group position="center" style={{ padding: '2rem' }}>
          <Loader color="orange" size="lg" />
        </Group>
      ) : (
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          themeSystem="standard"
          height="auto"
          initialDate={new Date(currentCalendarYear, currentCalendarMonth - 1, 1)}
          headerToolbar={{
            right: 'prev,next today',
            left: 'title',
            center: '',
          }}
          datesSet={handleDatesSet}
          events={calendarEvents}
          displayEventTime={false}
          eventClick={(info) => {
            // Prevent default navigation
            info.jsEvent.preventDefault();

            // Extract slug from the URL
            let eventSlug = null;
            if (info.event.url) {
              // Get the last part of the URL which should be the slug
              const urlParts = info.event.url.split('/');
              eventSlug = urlParts[urlParts.length - 1];
            }

            if (!eventSlug) {
              console.error('Could not extract slug from URL:', info.event.url);
              return;
            }

            // Try to find the event in upcoming and past events first (more complete data)
            let paginatedEvents: Event[] = [];
            if (upcomingEvents?.length) {
              paginatedEvents = [...paginatedEvents, ...upcomingEvents];
            }
            if (pastEvents?.length) {
              paginatedEvents = [...paginatedEvents, ...pastEvents];
            }

            // First check in paginated events, then fall back to calendar events
            let fullEvent = paginatedEvents.find((e: Event) => e.slug === eventSlug);

            // If not found in paginated events, check in calendar events
            if (!fullEvent && currentEvents) {
              fullEvent = currentEvents.find((e: Event) => e.slug === eventSlug);
            }

            // If not found in current events, check in initial events
            if (!fullEvent) {
              fullEvent = initialEvents.find((e: Event) => e.slug === eventSlug);
            }

            if (fullEvent && onEventClick) {
              // Call the handler with the complete event object
              onEventClick(fullEvent);
            } else {
              console.error('No matching event found for slug:', eventSlug);
            }
          }}
        />
      )}
    </Box>
  );
};

export default FullCalendarView;
